import _get from 'lodash/get';
import {
  useFormCategoriesSearchListLazyQuery,
  useFormTemplatesSearchListLazyQuery,
  useSectorsSearchListLazyQuery,
  useSiteAreasSearchListLazyQuery,
  useSitesSearchListLazyQuery,
  useTasksSearchListLazyQuery,
  useFeFilterPresetsLazyQuery,
  FormTemplateFilterInputObject,
  FilterGroupingEnum,
} from 'graphql-common';
import { GetLoadOptions } from '@lib/components/Select/types';
import { getFormCategoriesLoadOptions as getFormCategoriesLoadOptionsFn } from 'utils/fetch/searchLists/formCategoriesSearchList';
import { getFormTemplatesLoadOptions as getFormTemplatesLoadOptionsFn } from 'utils/fetch/searchLists/formTemplatesSearchList';
import MonitoringFilterFieldNames from '@lib/enums/fieldNames/monitoringFilterFieldNames';
import getFilterFieldNameByMonitoringEntity from 'routes/Dashboard/Monitoring/utils/getFilterFieldNameByMonitoringEntity';
import { MonitoringEntity } from 'routes/Dashboard/Monitoring/enums';

export type UseQueryResult = {
  getFormCategoriesLoadOptions: GetLoadOptions;
  getTemplatesLoadOptions: GetLoadOptions;
  fetchSiteAreasSearchListLazyQuery: ReturnType<
    typeof useSiteAreasSearchListLazyQuery
  >[0];
  fetchSectorsSearchListLazyQuery: ReturnType<
    typeof useSectorsSearchListLazyQuery
  >[0];
  fetchTasksSearchListLazyQuery: ReturnType<
    typeof useTasksSearchListLazyQuery
  >[0];
  fetchSitesSearchListLazyQuery: ReturnType<
    typeof useSitesSearchListLazyQuery
  >[0];
  fetchFeFilterPresetsLazyQuery: ReturnType<
    typeof useFeFilterPresetsLazyQuery
  >[0];
};

export default function useQueries({
  monitoringEntity,
}: {
  monitoringEntity: MonitoringEntity;
}): UseQueryResult {
  const fieldNameFormCategory = getFilterFieldNameByMonitoringEntity(
    monitoringEntity,
    MonitoringFilterFieldNames.FormCategory,
  );

  // Sites query
  const [fetchSitesSearchListLazyQuery] = useSitesSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  // Form Templates query
  const [formTemplatesLazyQuery] = useFormTemplatesSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const getTemplatesLoadOptions: GetLoadOptions = (name, values) => {
    const selectedFormCategories = _get(values, fieldNameFormCategory);
    let selectedFormCategoryIds: string[] = [];
    if (
      Array.isArray(selectedFormCategories) &&
      selectedFormCategories.length
    ) {
      selectedFormCategoryIds = selectedFormCategories.map(
        ({ value }) => value,
      );
    }
    const additionalFilters: FormTemplateFilterInputObject[] = [];
    if (selectedFormCategoryIds) {
      additionalFilters.push({
        categoryId: {
          grouping: FilterGroupingEnum.And,
          predicate: { in: selectedFormCategoryIds },
          filterGroup: 'categoryId',
        },
      });
    }
    return getFormTemplatesLoadOptionsFn(
      formTemplatesLazyQuery,
      additionalFilters,
    );
  };

  // Form categories query
  const [fetchFormCategoriesLazyQuery] = useFormCategoriesSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getFormCategoriesLoadOptions: GetLoadOptions = () =>
    getFormCategoriesLoadOptionsFn(fetchFormCategoriesLazyQuery);

  // Site areas query
  const [fetchSiteAreasSearchListLazyQuery] = useSiteAreasSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  // Sectors query
  const [fetchSectorsSearchListLazyQuery] = useSectorsSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  // Tasks query
  const [fetchTasksSearchListLazyQuery] = useTasksSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  // FeFilterPresets query
  const [fetchFeFilterPresetsLazyQuery] = useFeFilterPresetsLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  return {
    fetchFeFilterPresetsLazyQuery,
    fetchSectorsSearchListLazyQuery,
    fetchSiteAreasSearchListLazyQuery,
    fetchSitesSearchListLazyQuery,
    fetchTasksSearchListLazyQuery,
    getFormCategoriesLoadOptions,
    getTemplatesLoadOptions,
  };
}
